const API_BASE = '/api';

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  return response.json();
}

export const api = {
  // Resources
  getResources: () => request('/resources'),
  getResource: (id: number) => request(`/resources/${id}`),
  createResource: (data: any) => request('/resources', { method: 'POST', body: JSON.stringify(data) }),
  updateResource: (id: number, data: any) => request(`/resources/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteResource: (id: number) => request(`/resources/${id}`, { method: 'DELETE' }),
  bulkDeleteResources: (ids: number[]) =>
    request('/resources/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }),
  uploadResources: (data: any[]) => request('/resources/upload', { method: 'POST', body: JSON.stringify({ data }) }),
  getResourceFunctions: () => request('/resources/meta/functions'),

  // Capabilities
  getCapabilities: () => request('/capabilities'),
  getCapability: (id: number) => request(`/capabilities/${id}`),
  createCapability: (data: any) => request('/capabilities', { method: 'POST', body: JSON.stringify(data) }),
  updateCapability: (id: number, data: any) =>
    request(`/capabilities/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCapability: (id: number) => request(`/capabilities/${id}`, { method: 'DELETE' }),
  bulkDeleteCapabilities: (ids: number[]) =>
    request('/capabilities/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }),
  uploadCapabilities: (data: any[]) =>
    request('/capabilities/upload', { method: 'POST', body: JSON.stringify({ data }) }),

  // Outcomes
  getOutcomes: () => request('/outcomes'),
  getOutcome: (id: number) => request(`/outcomes/${id}`),
  createOutcome: (data: any) => request('/outcomes', { method: 'POST', body: JSON.stringify(data) }),
  updateOutcome: (id: number, data: any) => request(`/outcomes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteOutcome: (id: number) => request(`/outcomes/${id}`, { method: 'DELETE' }),
  updateOutcomeTags: (id: number, tags: string[]) =>
    request(`/outcomes/${id}/tags`, { method: 'PATCH', body: JSON.stringify({ tags }) }),
  uploadOutcomes: (data: any[]) => request('/outcomes/upload', { method: 'POST', body: JSON.stringify({ data }) }),
  getOutcomeLabels: () => request('/outcomes/meta/labels'),

  // Links
  getLinks: (capabilityId: number) => request(`/links/capability/${capabilityId}`),
  getAggregateLinks: (capabilityId: number) => request(`/links/capability/${capabilityId}/aggregate`),
  getAllUsersLinks: (capabilityId: number) => request(`/links/capability/${capabilityId}/all-users`),
  createLink: (resourceId: number, capabilityId: number) =>
    request('/links', { method: 'POST', body: JSON.stringify({ resourceId, capabilityId }) }),
  deleteLink: (id: number) => request(`/links/${id}`, { method: 'DELETE' }),
  bulkLink: (capabilityId: number, resourceIds: number[]) =>
    request('/links/bulk', { method: 'POST', body: JSON.stringify({ capabilityId, resourceIds }) }),
  importLinksCsv: (rows: any[]) =>
    request('/links/import-csv', { method: 'POST', body: JSON.stringify({ rows }) }),

  // Ratings
  getCriticalityRatings: (capabilityId: number) => request(`/ratings/criticality/capability/${capabilityId}`),
  setCriticalityRating: (resourceId: number, capabilityId: number, score: number) =>
    request('/ratings/criticality', { method: 'PUT', body: JSON.stringify({ resourceId, capabilityId, score }) }),
  getAggregateCriticality: (capabilityId: number) =>
    request(`/ratings/criticality/capability/${capabilityId}/aggregate`),

  getMaturityRating: (resourceId: number) => request(`/ratings/maturity/resource/${resourceId}`),
  setMaturityRating: (resourceId: number, score: number) =>
    request('/ratings/maturity', { method: 'PUT', body: JSON.stringify({ resourceId, score }) }),

  getRefinements: (resourceId: number, capabilityId: number) =>
    request(`/ratings/refinements/resource/${resourceId}/capability/${capabilityId}`),
  getUserRefinement: (resourceId: number, capabilityId: number) =>
    request(`/ratings/refinements/user/resource/${resourceId}/capability/${capabilityId}`),
  setRefinement: (resourceId: number, capabilityId: number, text: string) =>
    request('/ratings/refinements', { method: 'PUT', body: JSON.stringify({ resourceId, capabilityId, text }) }),

  getCombinedRatings: (capabilityId: number) => request(`/ratings/combined/capability/${capabilityId}`),
  importMaturityCsv: (rows: any[]) =>
    request('/ratings/import-maturity-csv', { method: 'POST', body: JSON.stringify({ rows }) }),
  importCriticalityCsv: (rows: any[]) =>
    request('/ratings/import-csv', { method: 'POST', body: JSON.stringify({ rows }) }),

  // Qualification imports
  importLinkQualificationsCsv: (rows: any[]) =>
    request('/link-qualifications/import-csv', { method: 'POST', body: JSON.stringify({ rows }) }),
  importWeightMaturityQualificationsCsv: (rows: any[]) =>
    request('/weight-maturity-qualifications/import-csv', { method: 'POST', body: JSON.stringify({ rows }) }),

  // VRIO
  getShortlist: () => request('/vrio/shortlist'),
  getVRIORating: (resourceId: number) => request(`/vrio/resource/${resourceId}`),
  setVRIORating: (resourceId: number, data: any) =>
    request(`/vrio/resource/${resourceId}`, { method: 'PUT', body: JSON.stringify(data) }),
  getAggregateVRIO: () => request('/vrio/aggregate'),
  getResourceAggregateVRIO: (resourceId: number) => request(`/vrio/resource/${resourceId}/aggregate`),

  // Link Qualifications
  getLinkQualificationResources: (capabilityId: number) =>
    request(`/link-qualifications/capability/${capabilityId}/resources`),
  getLinkQualificationSelections: (capabilityId: number) =>
    request(`/link-qualifications/capability/${capabilityId}/selections`),
  setLinkQualificationSelections: (capabilityId: number, selections: any[]) =>
    request(`/link-qualifications/capability/${capabilityId}/selections`, {
      method: 'PUT',
      body: JSON.stringify({ selections }),
    }),
  getAllCapabilitiesWithStats: () => request('/link-qualifications/all-capabilities'),
  getQualifiedResources: (capabilityId: number) =>
    request(`/link-qualifications/capability/${capabilityId}/qualified-resources`),
  getAllQualifiedResources: () => request('/link-qualifications/all-qualified-resources'),

  // Gap Analysis
  getGapAnalysisMatrix: () => request('/gap-analysis/matrix'),

  // Strategic Portfolio (VRIO analysis)
  getVRIOAnalysis: () => request('/vrio/analysis'),

  // Admin
  getToggles: () => request('/admin/toggles'),
  setToggle: (key: string, enabled: boolean) =>
    request(`/admin/toggles/${key}`, { method: 'PUT', body: JSON.stringify({ enabled }) }),
  getStats: () => request('/admin/stats'),
  getUserStats: () => request('/admin/user-stats'),
  getSettings: () => request('/admin/settings'),
  refreshDevFromProd: () => request('/admin/db-refresh', { method: 'POST' }),

  // Generic helpers (for flexibility)
  get: (path: string) => request(path),
  put: (path: string, data: any) => request(path, { method: 'PUT', body: JSON.stringify(data) }),
  post: (path: string, data: any) => request(path, { method: 'POST', body: JSON.stringify(data) }),
  patch: (path: string, data: any) => request(path, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (path: string) => request(path, { method: 'DELETE' }),
};
